import React, { useEffect } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { router, usePathname } from "expo-router";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { EmailInput } from "@/components/ui/EmailInput";
import { RadioButtonGroup } from "@/components/ui/RadioButtonGroup";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { useTheme } from "@/hooks/useTheme";
import { useApply } from "@/contexts/ApplyContext";
import { formatPhoneNumber, validatePhoneNumber } from "@/utils/phoneValidation";

const STEP_INDEX = 1;

const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
];

const REFERRAL_SOURCES = ["Facebook", "Google", "Referral", "Other"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InfoScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep } = useApply();
  const pathname = usePathname();

  // Track current step (only when this screen is active)
  useEffect(() => {
    if (pathname.includes("/info")) {
      updateStep(STEP_INDEX);
    }
  }, [pathname, updateStep]);

  const phoneValid = validatePhoneNumber(formData.phone);
  const phonePartial = formData.phone.length > 0 && !phoneValid;
  const zipValid = formData.zip.length === 5;
  const zipPartial = formData.zip.length > 0 && !zipValid;

  const isFormValid =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    EMAIL_REGEX.test(formData.email.trim()) &&
    phoneValid &&
    formData.streetLine1.trim() !== "" &&
    formData.city.trim() !== "" &&
    formData.state !== "" &&
    zipValid &&
    formData.referralSource !== "" &&
    (formData.referralSource !== "Referral" && formData.referralSource !== "Other"
      ? true
      : formData.referralName.trim() !== "");

  const handleContinue = () => {
    router.push("/apply/physical");
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Personal Information</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>Tell us about yourself</ThemedText>

        {/* Previous Employee */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Previous Employment</ThemedText>
          <ThemedText style={[styles.questionLabel, { color: colors.text }]}>
            Have you worked for RockForce in the past?
          </ThemedText>
          <RadioButtonGroup
            options={["Yes", "No"]}
            value={formData.previouslyWorked === null ? undefined : formData.previouslyWorked ? "Yes" : "No"}
            onChange={(item) => {
              updateFormData({ previouslyWorked: item === "Yes" });
            }}
            orientation="horizontal"
            style={styles.toggle}
          />
        </View>

        {/* Legal Name */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Legal Name</ThemedText>
          <View style={styles.row}>
            <Input
              icon="person"
              placeholder="First Name *"
              value={formData.firstName}
              onChangeText={(text) => updateFormData({ firstName: text })}
              containerStyle={styles.flexInput}
            />
            <Input
              placeholder="M.I."
              value={formData.middleInitial}
              onChangeText={(text) => updateFormData({ middleInitial: text.slice(0, 1).toUpperCase() })}
              maxLength={1}
              containerStyle={styles.smallInput}
            />
          </View>
          <Input
            icon="person"
            placeholder="Last Name *"
            value={formData.lastName}
            onChangeText={(text) => updateFormData({ lastName: text })}
            containerStyle={styles.input}
          />
          <Input
            icon="badge"
            placeholder="Preferred Name (optional)"
            value={formData.preferredName}
            onChangeText={(text) => updateFormData({ preferredName: text })}
            containerStyle={styles.input}
          />
        </View>

        {/* Contact Info */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Contact Information</ThemedText>
          <EmailInput
            placeholder="Email Address *"
            value={formData.email}
            onChangeText={(text) => updateFormData({ email: text })}
          />
          <View style={{ gap: 4 }}>
            <Input
              icon="phone"
              placeholder="Phone Number *"
              value={formData.phone}
              onChangeText={(text) => updateFormData({ phone: formatPhoneNumber(text) })}
              keyboardType="phone-pad"
              containerStyle={{
                ...styles.input,
                borderColor: phoneValid
                  ? colors.emerald?.border || "green"
                  : phonePartial
                    ? colors.ruby?.border || "red"
                    : undefined,
                borderWidth: phoneValid || phonePartial ? 1 : StyleSheet.hairlineWidth,
              }}
            />
            {phoneValid && (
              <View style={{ position: "absolute", right: 12, top: 12 }}>
                <MaterialCommunityIcons name="check-circle" size={20} color={colors.emerald?.text || "green"} />
              </View>
            )}
            {phonePartial && (
              <ThemedText style={{ fontSize: 12, color: colors.ruby?.text || "red" }}>
                Enter a valid 10-digit phone number
              </ThemedText>
            )}
          </View>
        </View>

        {/* Address */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Address</ThemedText>
          <Input
            icon="location-on"
            placeholder="Street Address Line 1 *"
            value={formData.streetLine1}
            onChangeText={(text) => updateFormData({ streetLine1: text })}
            containerStyle={styles.input}
          />
          <Input
            placeholder="Street Address Line 2"
            value={formData.streetLine2}
            onChangeText={(text) => updateFormData({ streetLine2: text })}
            containerStyle={styles.input}
          />
          <View style={styles.row}>
            <Input
              placeholder="City *"
              value={formData.city}
              onChangeText={(text) => updateFormData({ city: text })}
              containerStyle={styles.flexInput}
            />
            <View style={styles.stateInput}>
              <InlineDropdown
                options={US_STATES}
                value={formData.state}
                placeholder="State *"
                onSelect={(value) => updateFormData({ state: value })}
              />
            </View>
          </View>
          <Input
            placeholder="ZIP Code *"
            value={formData.zip}
            onChangeText={(text) => updateFormData({ zip: text.replace(/\D/g, "").slice(0, 5) })}
            keyboardType="number-pad"
            maxLength={5}
            containerStyle={{
              ...styles.input,
              maxWidth: 150,
              borderColor: zipValid
                ? colors.emerald?.border || "green"
                : zipPartial
                  ? colors.ruby?.border || "red"
                  : undefined,
              borderWidth: zipValid || zipPartial ? 1 : StyleSheet.hairlineWidth,
            }}
          />
        </View>

        {/* Office */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Office Location</ThemedText>
          <Input
            icon="business"
            placeholder="Which office are you applying to?"
            value={formData.office}
            onChangeText={(text) => updateFormData({ office: text })}
            containerStyle={styles.input}
          />
        </View>

        {/* Referral */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>How did you hear about us?</ThemedText>
          <RadioButtonGroup
            options={REFERRAL_SOURCES}
            value={formData.referralSource || undefined}
            onChange={(item) => {
              updateFormData({ referralSource: item || "", referralName: "" });
            }}
            orientation="vertical"
            style={styles.toggle}
          />
          {formData.referralSource === "Referral" && (
            <Input
              icon="person-add"
              placeholder="Who referred you? *"
              value={formData.referralName}
              onChangeText={(text) => updateFormData({ referralName: text })}
              containerStyle={styles.input}
            />
          )}
          {formData.referralSource === "Other" && (
            <Input
              icon="edit"
              placeholder="Please specify *"
              value={formData.referralName}
              onChangeText={(text) => updateFormData({ referralName: text })}
              containerStyle={styles.input}
            />
          )}
        </View>

        <View style={styles.buttonRow}>
          <Button
            text="Back"
            icon="arrow-left"
            iconPosition="left"
            iconSize={18}
            iconColor={colors.text}
            onPress={() => router.push("/apply")}
            style={[styles.backButton, { borderColor: colors.border }]}
            textStyle={[styles.backButtonText, { color: colors.text }]}
          />
          <Button
            text="Continue"
            icon="arrow-right"
            iconPosition="right"
            iconSize={18}
            iconColor="white"
            onPress={handleContinue}
            enabled={isFormValid}
            style={[
              styles.continueButton,
              {
                backgroundColor: isFormValid ? colors.primary : colors.border,
                opacity: isFormValid ? 1 : 0.5,
              },
            ]}
            textStyle={styles.continueButtonText}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 8,
  },
  section: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  questionLabel: {
    fontSize: 14,
  },
  toggle: {
    marginTop: 4,
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
  input: {
    minWidth: "100%",
  },
  flexInput: {
    flex: 1,
  },
  smallInput: {
    width: 60,
  },
  stateInput: {
    width: 140,
    zIndex: 10,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
  backButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 52,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
  continueButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 52,
  },
  continueButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
